import * as THREE from "three";
import {Interpolation} from "../../math/interpolation.js";
import {RacerMovementSampler} from "./movement/racerMovementSampler.js";
import {RacerTurbulence} from "./turbulence/racerTurbulence.js";
import {RacerTurbulenceComponentHover} from "./turbulence/component/racerTurbulenceComponentHover.js";
import {RacerTrail} from "./trail/racerTrail.js";
import {RacerTrailProperties} from "./trail/racerTrailProperties.js";
import {RaceRenderOrder} from "../../raceRenderOrder.js";
import {StyleUtils} from "../../utils/styleUtils.js";
import {Globals} from "../../../global/globals.js";
import {CameraTargetable} from "../../camera/cameraTargetable.js";
import {lerp} from "three/src/math/MathUtils.js";
import {RacerGlitcher} from "./racerGlitcher.js";

/**
 * A racer
 */
export default class Racer extends CameraTargetable {
    static SCALE = StyleUtils.getFloat("--racer-model-scale");
    static EPSILON = .0001;
    static LOOK_AHEAD_TURN = StyleUtils.getFloat("--racer-look-ahead-turn");
    static LOOK_AHEAD_BANK = StyleUtils.getFloat("--racer-look-ahead-bank");
    static BANK_MAGNITUDE = StyleUtils.getFloat("--racer-bank-magnitude");
    static TRAIL_STARTINGX = StyleUtils.getFloat("--racer-trail-x");
    static TRAIL_STARTINGY = StyleUtils.getFloat("--racer-trail-y");

    /**
     * Construct a racer
     * @param {RacerData} data Racer data
     * @param {TrackTracer} tracer A track tracer
     * @param {Vector3} offset The relative offset on the track
     */
    constructor(data, tracer, offset, racerCount=2) {
        super();

        this.data = data;
        this.movementSampler = new RacerMovementSampler(data.movement);
        this.turbulence = new RacerTurbulence([
            new RacerTurbulenceComponentHover(.61, .015),
            new RacerTurbulenceComponentHover(.483, .02)
        ]);
        this.tracer = tracer;
        this.offset = offset;
        this.position = offset.clone();
        this.positionPrevious = offset.clone();
        this.direction = new THREE.Vector3(1, 0, 0);
        this.directionPrevious = this.direction.clone();
        this.directionRender = this.direction.clone();
        this.positionTarget = new THREE.Vector3();
        this.yaw = this.yawPrevious = 0;
        this.roll = this.rollPrevious = 0;
        this.rotation = new THREE.Euler();
        this.ship_scale = Racer.SCALE;
        let trailx = Racer.TRAIL_STARTINGX;
        let traily = Racer.TRAIL_STARTINGY;
        if(racerCount > 6) {
            this.ship_scale = Racer.SCALE / 1.75;
            trailx = Racer.TRAIL_STARTINGX / 1.75;
            traily = Racer.TRAIL_STARTINGY / 1.75;
        } else if (racerCount > 2) {
            this.ship_scale = Racer.SCALE / 1.5;
            trailx = Racer.TRAIL_STARTINGX / 1.5;
            traily = Racer.TRAIL_STARTINGY / 1.5;
        }

        this.trail = new RacerTrail(
            new RacerTrailProperties(0xff0000),
            new THREE.Vector3(trailx, traily, 0),
            data.racerSpecial);
        this.ship = null;
        this.time = 0;
        this.moved = 0;
        this.movedPrevious = this.moved;
        this.speedPrevious = this.speed = 0;
        this.accelerationPrevious = this.acceleration = 0;
        this.finished = -1;
        this.material = null;
        this.glitchedTextures = null;
        this.trail_color_update = 0;
        this.aggression_boost = 0;
        this.current_racer = data.currentRacer;
        this.special_racer = data.racerSpecial;
        this.trail.teleport(this.position);
    }

    /**
     * Get the position of the targetable
     * @param {number} time The time interpolation in the range [0, 1]
     * @returns {Vector3} The position
     */
    getPosition(time) {
        return this.ship.position;
    }

    /**
     * Get the direction of the targetable
     * @param {number} time The time interpolation in the range [0, 1]
     * @returns {Vector3} The normalized direction
     */
    getDirection(time) {
        return this.directionRender;
    }

    /**
     * Get the distance of the targetable on the track\
     * @param {number} time The time interpolation in the range [0, 1]
     * @returns {number} The distance
     */
    getDistance(time) {
        return lerp(this.movedPrevious, this.moved, time);
    }

    /**
     * Get the index of the track piece this targetable is on
     * @returns {number} The index
     */
    getTrackPieceIndex() {
        return this.tracer.piece.index;
    }

    /**
     * Set this racer to finished
     */
    setFinished() {
        this.finished = this.data.finalStanding;
    }

    /**
     * Load the model for this racer
     * @param {WebGLRenderer} renderer The renderer
     * @returns {Promise} A promise which resolves when the racer was loaded
     */
    load(renderer) {
        return new Promise(resolve => {
            Globals.MODEL_CACHE.load(this.data.urlShipModel).then(ship => {
                this.ship = ship.clone();

                this.ship.traverse(node => {
                    if (node instanceof THREE.Mesh) {
                        node.castShadow = true;
                        node.material.roughness = 0.1;
                        node.material.transparent = true;
                        node.material.alphaTest = .50;
                        node.rotateY(Math.PI * -.5);
                        node.scale.x = node.scale.y = node.scale.z = this.ship_scale;

                        this.material = node.material;
                    }
                });

                this.ship.position.copy(this.position);
                this.ship.renderOrder = RaceRenderOrder.ORDER_SHIPS;

                resolve();

                if (this.material && this.material.map && this.data.glitched)
                    new RacerGlitcher(this.material.map).glitch(renderer).then(glitched => {
                        this.glitchedTextures = glitched;

                        resolve();
                    });
                else
                    resolve();
            });
        });
    }

    /**
     * Glitch the texture
     */
    glitch() {
        this.material.map = this.glitchedTextures[Math.floor(Math.random() * this.glitchedTextures.length)];
    }

    /**
     * Update the racer state
     * @param {number} delta The time delta
     */
    update(delta) {
        this.time += delta;

        this.positionPrevious.copy(this.position);
        this.yawPrevious = this.yaw;
        this.rollPrevious = this.roll;

        /* Setup Aggression Boost Check */
        this.boostPrevious = this.aggression_boost;
        this.aggression_boost = this.movementSampler.boostingCheck(this.time);
        if(this.aggression_boost !== this.boostPrevious) {
            this.trail_color_update = 1;
        }

        this.accelerationPrevious = this.acceleration;
        this.speedPrevious = this.speed;
        this.movedPrevious = this.moved;
        
        this.moved = this.movementSampler.sample(this.time);

        if (this.moved !== -1) {
            this.speed = this.moved - this.movedPrevious;
            this.acceleration = this.speed - this.speedPrevious;
        }

        this.tracer.advance(this.speed);

        this.yaw = this.tracer.trace(this.position, this.offset);
        this.tracer.trace(
            this.positionTarget,
            this.offset,
            Math.max(Racer.EPSILON, this.speed * Racer.LOOK_AHEAD_TURN));

        this.yaw = Math.atan2(
            this.position.z - this.positionTarget.z,
            this.positionTarget.x - this.position.x);

        this.tracer.trace(
            this.positionTarget,
            this.offset,
            Math.max(Racer.EPSILON, this.speed * Racer.LOOK_AHEAD_BANK));

        this.roll = -Interpolation.radiansDelta(
            Math.atan2(
                this.position.z - this.positionTarget.z,
                this.positionTarget.x - this.position.x),
            this.yaw) * Racer.BANK_MAGNITUDE;

        this.directionPrevious.copy(this.direction);
        this.direction.copy(this.position).sub(this.positionPrevious).normalize();
    }

    /**
     * Render a frame
     * @param {number} time The time interpolation in the range [0, 1]
     * @param {Blip} blip The blip to update
     */
    render(time, blip) {
        if (this.ship) {
            if (this.glitchedTextures)
                this.glitch();

            Interpolation.lerpVector(this.ship.position, this.positionPrevious, this.position, time);
            Interpolation.lerpVector(this.directionRender, this.directionPrevious, this.direction, time);

            const yaw = Interpolation.lerpRadians(this.yawPrevious, this.yaw, time);

            blip.setPosition(this.ship.position.x, this.ship.position.z, -180 * yaw / Math.PI);

            this.rotation.set(
                Interpolation.lerpRadians(this.rollPrevious, this.roll, time),
                Math.PI + yaw,
                0,
                "YXZ");

            this.ship.quaternion.setFromEuler(this.rotation);

            this.turbulence.apply(this.ship.position, this.ship.quaternion, Interpolation.lerp(this.movedPrevious, this.moved, time));
            
            /* Update Trail Color If Boosting Changing On of Off */
            if(this.trail_color_update == 1) {
                this.trail.showAggressionBoost(this.aggression_boost);
                this.trail_color_update = 0;
            }
            
            this.trail.moveHead(this.ship.position, this.ship.quaternion);
            this.trail.flare.setIntensity(
                Interpolation.lerp(this.speedPrevious, this.speed, time),
                Interpolation.lerp(this.accelerationPrevious, this.acceleration, time));
        }
    }
}